import React, {useReducer, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

const useFetchRecordsReducer = (reversed) => {
  const [{nextPage, records, isFetching}, dispatch] = useReducer(
    (state, action) => {
      switch (action.type) {
        case 'SET_IS_FETCHING':
          return {
            ...state,
            isFetching: true,
          };
        case 'SET_FETCH_SUCCEEDED':
          return {
            ...state,
            nextPage: action.pageCount > state.nextPage ? state.nextPage + 1 : null,
            records: reversed ? [...action.newRecords, ...state.records] : [...state.records, ...action.newRecords],
            isFetching: false,
          };
        case 'SET_FETCH_FAILED':
          return {
            ...state,
            isFetching: false,
          };
        default:
          return state;
      }
    },
    {
      nextPage: 1,
      records: [],
      isFetching: false,
    },
  );

  const setIsFetching = () => dispatch({type: 'SET_IS_FETCHING'});
  const setFetchSucceeded = ({newRecords, pageCount}) => dispatch({type: 'SET_FETCH_SUCCEEDED', newRecords, pageCount});
  const setFetchFailed = () => dispatch({type: 'SET_FETCH_FAILED'});

  return {
    nextPage,
    records,
    isFetching,
    hasNextPage: nextPage !== null,
    setIsFetching,
    setFetchSucceeded,
    setFetchFailed,
  };
};

const ScrollToLoadWrapper = ({className, fetchFunc, fetchFuncParams, list, anchor, reversed}) => {
  const {
    nextPage,
    records,
    isFetching,
    hasNextPage,
    setIsFetching,
    setFetchSucceeded,
    setFetchFailed,
  } = useFetchRecordsReducer(reversed);

  const wrapperRef = useRef(null);
  const anchorRef = useRef(null);

  const fetchMoreRecords = () => {
    setIsFetching();
    return fetchFunc({...fetchFuncParams, page: nextPage, perPage: 20})
      .then((response) => {
        const {records: newRecords, pageCount} = response;
        setFetchSucceeded({newRecords, pageCount});
        return response;
      })
      .catch((error) => {
        setFetchFailed();
        return Promise.reject(error);
      });
  };

  useEffect(() => {
    if (!wrapperRef.current) return;
    fetchMoreRecords().then(() => {
      if (reversed) {
        wrapperRef.current.scrollTop = wrapperRef.current.scrollHeight;
      }
    });
  }, [wrapperRef, reversed]);

  useEffect(() => {
    if (!wrapperRef.current || !anchorRef.current) return;

    const handleWrapperScroll = () => {
      if (isFetching || !hasNextPage) return;
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const anchorRect = anchorRef.current.getBoundingClientRect();
      if (!reversed && wrapperRect.top + wrapperRect.height >= anchorRect.top) {
        fetchMoreRecords();
      } else if (reversed && anchorRect.top + anchorRect.height >= wrapperRect.top) {
        const wrapperEl = wrapperRef.current;
        const prevScrollHeight = wrapperEl.scrollHeight;
        fetchMoreRecords().then(() => {
          wrapperEl.scrollTop = wrapperEl.scrollHeight - prevScrollHeight;
        });
      }
    };

    wrapperRef.current.addEventListener('scroll', handleWrapperScroll);
    return () => {
      wrapperRef.current.removeEventListener('scroll', handleWrapperScroll);
    };
  }, [wrapperRef, isFetching]);

  return reversed ? (
    <div ref={wrapperRef} className={classNames(className)}>
      {anchor({anchorRef, hasNextPage, isFetching, fetchMoreRecords})}
      {list({records})}
    </div>
  ) : (
    <div ref={wrapperRef} className={classNames(className)}>
      {list({records})}
      {anchor({anchorRef, hasNextPage, isFetching, fetchMoreRecords})}
    </div>
  );
};

ScrollToLoadWrapper.propTypes = {
  className: PropTypes.string,
  fetchFunc: PropTypes.func.isRequired,
  fetchFuncParams: PropTypes.object.isRequired,
  perPage: PropTypes.number.isRequired,
  list: PropTypes.func.isRequired,
  anchor: PropTypes.func.isRequired,
  reversed: PropTypes.bool.isRequired,
};

ScrollToLoadWrapper.defaultProps = {
  fetchFuncParams: {},
  perPage: 20,
  reversed: false,
};

export default ScrollToLoadWrapper;
